import React from 'react'
import useHover from 'utils/hooks/useHover'
import { useRouteMatch } from 'react-router-dom'
import { VersionListItemFragment } from 'gqlTypes'
import {
  versionedRoutes,
  getAllTemplatesForVersionedRoute,
} from 'constants/routes'
import { useFlag } from 'constants/flags'
import VersionItem from 'components/VersionItem/VersionItem'
import { ShareVersionItemOptions } from './ShareVersionItemOptions'

export interface ShareVersionItemProps {
  version: VersionListItemFragment
  versionShortId: string
  shareIdentifier: string
  shareName: string
  totalCount: number
  kind: VersionListItemFragment['kind']
  latestStar?: VersionListItemFragment
  isLibrary: boolean
  canEdit: boolean
  downloadEnabled: boolean
  isFirstVersion?: boolean
}

// can't use the functions from CwvRoutes because of circular dependencies
const getRoute = (
  path: string,
  isComponentsWebViewOn: boolean,
  shareID: string,
  versionShortId: string
) => {
  if (isComponentsWebViewOn) {
    if (getAllTemplatesForVersionedRoute('CWV_SYMBOLS').find(r => r === path)) {
      return versionedRoutes.CWV_SYMBOLS.VERSION.create({
        shareID,
        versionShortId,
      })
    }
    if (
      getAllTemplatesForVersionedRoute('CWV_LAYER_STYLES').find(r => r === path)
    ) {
      return versionedRoutes.CWV_LAYER_STYLES.VERSION.create({
        shareID,
        versionShortId,
      })
    }
    if (
      getAllTemplatesForVersionedRoute('CWV_TEXT_STYLES').find(r => r === path)
    ) {
      return versionedRoutes.CWV_TEXT_STYLES.VERSION.create({
        shareID,
        versionShortId,
      })
    }
    if (
      getAllTemplatesForVersionedRoute('CWV_COLOR_VARIABLES').find(
        r => r === path
      )
    ) {
      return versionedRoutes.CWV_COLOR_VARIABLES.VERSION.create({
        shareID,
        versionShortId,
      })
    }
  }

  return versionedRoutes.SHARE_VIEW.VERSION.create({
    shareID,
    versionShortId,
  })
}

export const ShareVersionItem = ({
  version,
  versionShortId,
  shareIdentifier,
  shareName,
  totalCount,
  kind,
  latestStar,
  isLibrary,
  canEdit,
  downloadEnabled,
  isFirstVersion,
}: ShareVersionItemProps) => {
  const [hovered] = useHover()
  const isComponentsWebViewOn = useFlag('components-web-view')
  const { path } = useRouteMatch()

  const to = getRoute(
    path,
    isComponentsWebViewOn,
    shareIdentifier,
    version.shortId
  )

  const isActive = versionShortId === version.shortId
  const isDisabled = !version.createdAfterVersioning
  const tooltip = !version.createdAfterVersioning ? (
    <span>Updates created before July 23, 2019 are not available</span>
  ) : undefined
  const createdAt = new Date(version.createdAt!)

  const actions = !isDisabled ? (
    <ShareVersionItemOptions
      isActive={isActive}
      hovered={hovered}
      to={to}
      documentUrl={version?.document?.url}
      createdAt={version.createdAt!}
      creatorName={version.creator?.name!}
      shareName={shareName}
      shareID={shareIdentifier}
      versionIdentifier={version.identifier}
      totalCount={totalCount}
      kind={kind}
      latestStar={latestStar}
      canEdit={canEdit}
      canStar={version.document?.renderStatus === 'FINISHED'}
      downloadEnabled={downloadEnabled}
    />
  ) : undefined

  return (
    <VersionItem
      type="revision"
      identifier={version.identifier}
      active={isActive}
      disabled={isDisabled}
      pathname={to}
      tooltip={tooltip}
      creator={version.creator?.name!}
      createdAt={version.createdAt}
      date={createdAt}
      userIsCreator={version.userIsCreator!}
      actions={actions}
      renderStatus={version.document?.renderStatus!}
      kind={kind}
      isLibrary={isLibrary}
      latestStar={latestStar}
      starOnHover={
        version.document?.renderStatus !== 'FINISHED' ? false : canEdit
      }
      isFirst={isFirstVersion}
    />
  )
}
